import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { BackLink, recordGate } from '@/components/record';
import PaymentDialog from '@/components/admin/PaymentDialog';
import { PAYMENT_KIND_STYLES } from '@/shared/constants';
import { PAYMENT_FIELDS } from '@/shared/fields';
import { money, paymentLabel, planName } from '@/shared/pricing';
import { committed, dayOf, idOf, logActivity } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/payments/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminPaymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: payment, loading, error, run } = useFetch(
    () => sdk.table('payments_c').select(PAYMENT_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/payments" label="Back to Payments" />;
  const gate = recordGate({ back, loading, record: payment, error, run, what: 'Payment' });
  if (gate) return gate;

  const orderId = idOf(payment.order_c);

  async function remove() {
    const res = await sdk.table('payments_c').remove(payment.Id);
    if (!committed(res, 'Payment deleted')) return false;
    logActivity('Payment', `Deleted ${money(payment.amount_c)}`, payment.Name);
    navigate('/admin/payments');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow={`${payment.kind_c || 'Payment'} · ${paymentLabel(payment.method_c)}`}
        title={money(payment.amount_c)}
        description={payment.Name}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete
              title="Delete this payment?"
              description="Revenue figures and the customer's receipts drop it. This cannot be undone."
              onConfirm={remove}
            />
          </>
        }
      />

      <Panel title="Payment" icon="Banknote">
        <dl className="divide-y divide-border">
          <DetailRow label="Reference">{payment.Name}</DetailRow>
          <DetailRow label="Order">
            {orderId ? (
              <Link to={`/admin/orders/${orderId}`} className="font-mono text-primary hover:underline">
                {payment.order_c?.Name ?? 'View order'}
              </Link>
            ) : null}
          </DetailRow>
          <DetailRow label="Plan">{planName(payment.plan_c)}</DetailRow>
          <DetailRow label="Kind">
            <StatusBadge value={payment.kind_c} styles={PAYMENT_KIND_STYLES} />
          </DetailRow>
          <DetailRow label="Amount">{money(payment.amount_c)}</DetailRow>
          <DetailRow label="Method">{paymentLabel(payment.method_c)}</DetailRow>
          <DetailRow label="Paid on">
            {payment.paid_on_c ? formatLocalDate(dayOf(payment.paid_on_c), DATE_FORMATS.LONG) : null}
          </DetailRow>
          <DetailRow label="Recorded">{formatLocalDate(payment.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
        </dl>
      </Panel>

      <PaymentDialog open={editOpen} onOpenChange={setEditOpen} payment={payment} onSaved={run} />
    </div>
  );
}
